import Decimal from 'decimal.js';
import type { FaixaMc, McBlocoResponse } from '@triade/contracts';
import { cn } from '@/lib/utils';
import { ehDecimal, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';

/** Cores dos selos de lucro (mesmos tokens do ProfitBadge): GREEN = verde, ORANGE = laranja, RED = vermelho. */
export const ESTILO_FAIXA_MC: Record<FaixaMc, string> = {
  GREEN: 'bg-success-bg text-success-ink',
  ORANGE: 'bg-warning-bg text-warning-ink',
  RED: 'bg-danger-bg text-danger-ink',
};

export const ROTULO_FAIXA_MC: Record<FaixaMc, string> = {
  GREEN: 'Cobre a despesa fixa',
  ORANGE: 'Não cobre a despesa fixa',
  RED: 'Prejuízo marginal',
};

/** Selo da faixa de MC. A faixa vem sempre da API; a tela so escolhe a cor e o rotulo. */
export function SeloFaixaMc({ faixa, rotulo, className }: { faixa: FaixaMc; rotulo?: string; className?: string }) {
  return (
    <span
      data-faixa-mc={faixa}
      className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-semibold', ESTILO_FAIXA_MC[faixa], className)}
    >
      {rotulo ?? ROTULO_FAIXA_MC[faixa]}
    </span>
  );
}

/** Custo de processo tratado como fixo absorvido: so aparece quando e maior que zero (comparacao, sem calculo). */
export function custoProcessoAbsorvidoPositivo(valor: string | null | undefined): boolean {
  return ehDecimal(valor) && new Decimal(valor).gt(0);
}

interface LinhaDecomposicao {
  rotulo: string;
  valor: string | undefined;
  destaque?: boolean;
}

/**
 * Bloco "Margem de contribuicao" de um preco (sugerido ou praticado): MC% e MC R$ em destaque, selo de faixa e a
 * decomposicao vinda da API. Valores em R$ so aparecem quando a API os manda (sem custo.ver vem so MC% e faixa).
 */
export function BlocoMargemContribuicao({
  titulo,
  bloco,
  custoProcessoAbsorvido,
}: {
  titulo: string;
  bloco: McBlocoResponse;
  custoProcessoAbsorvido?: string;
}) {
  const temValores = ehDecimal(bloco.mc);
  const linhas: LinhaDecomposicao[] = [
    { rotulo: 'Preço sem IPI', valor: bloco.precoSemIpi },
    { rotulo: 'Despesas variáveis', valor: bloco.despesasVariaveis },
    { rotulo: 'Custo financeiro', valor: bloco.custoFinanceiro },
    { rotulo: 'Custo variável', valor: bloco.custoVariavel },
    { rotulo: 'Margem de contribuição', valor: bloco.mc, destaque: true },
  ];
  const decomposicao = linhas.filter((l) => ehDecimal(l.valor));
  return (
    <section data-testid="mc-formacao" aria-label={`Margem de contribuição · ${titulo}`} className="flex flex-col gap-3 border-t border-dashed border-border px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-small font-semibold">Margem de contribuição <span className="font-normal text-ink-muted">· {titulo.toLowerCase()}</span></h3>
        <SeloFaixaMc faixa={bloco.faixa} />
      </div>
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        {temValores && (
          <span className="tabular text-title font-semibold" data-testid="mc-formacao-valor">
            {formatarMoeda(bloco.mc)}
          </span>
        )}
        <span className="tabular text-title font-semibold" data-testid="mc-formacao-pct">
          {formatarFracaoPercentual(bloco.mcPct)}
        </span>
      </div>
      {decomposicao.length > 0 && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-small">
          {decomposicao.map((l) => (
            <div key={l.rotulo} className={cn('contents', l.destaque && 'font-semibold')}>
              <dt className={cn('text-ink-secondary', l.destaque && 'border-t border-border pt-1 text-ink')}>{l.rotulo}</dt>
              <dd className={cn('tabular', l.destaque && 'border-t border-border pt-1')}>{formatarMoeda(l.valor)}</dd>
            </div>
          ))}
        </dl>
      )}
      {custoProcessoAbsorvidoPositivo(custoProcessoAbsorvido) && (
        <p className="text-caption text-ink-muted">Custo de processo {formatarMoeda(custoProcessoAbsorvido)} tratado como fixo absorvido.</p>
      )}
    </section>
  );
}

/** Ordena fracoes de MC% (so comparacao, sem calculo): nulos vao para o fim. Usado pelos comparadores das grades. */
export function compararMcPct(a: string | null | undefined, b: string | null | undefined): number {
  const va = ehDecimal(a) ? new Decimal(a) : null;
  const vb = ehDecimal(b) ? new Decimal(b) : null;
  if (va === null || vb === null) return va === null && vb === null ? 0 : va === null ? 1 : -1;
  return va.cmp(vb);
}
